import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./subscription_alert.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";

interface User {
  _id: string;
  name: string;
  phoneNumber: string;
  email: string;
  gender: string;
  age: string;
  weight: string;
  height: string;
  goal: string;
  activity: string;
  medicalConcern: string;
  startingBmi: string;
  languageCode: string;
  profilePicture: string;
  activePlans: any;
  historyPlans: any;
  weightEntries: object;
  dietPlan: object;
  createdAt: Date;
  updatedAt: Date;
}

const SubscriptionAlert = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedUsers, setExpandedUsers] = useState<{
    [key: string]: boolean;
  }>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const navigate = useNavigate();
  const isInitialRender = useRef(true);

  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllUsers();
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Session expired or invalid token! Redirecting to login.");
      handleLogout();
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-users-info`
      );
      setUsers(response.data.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  // const filteredUsers = users.filter((user) =>
  //   user.phoneNumber.includes(searchTerm)
  // );

  const filteredUsers = users.filter((user) => {
    // Check if user has activePlans and 'endDate' exists
    const hasActivePlans = user.activePlans && user.activePlans.length > 0;
    if (!hasActivePlans) return false;

    // Get the endDate of the active plan
    const endDate = user.activePlans[0]?.endDate;
    const today = new Date();
    const endDateObj = new Date(endDate);

    // Check if the difference between today's date and the 'endDate' is less than or equal to 3 days
    const timeDiff = Math.abs(endDateObj.getTime() - today.getTime());
    const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24));

    // Apply both filtering by endDate and search term
    return diffDays <= 3 && user.phoneNumber.includes(searchTerm);
  });

  const sendSubscriptionAlert = async (
    userPhoneNumber: string,
    userName: string
  ) => {
    try {
      const data = {
        phoneNumber: userPhoneNumber,
        title: "Your Subscription is ending soon!",
        description:
          "Hey " +
          userName +
          ", Your Subscription is going to expire soon. Please renew your subscription now to avoid loss of access of premium features. \nClick on the below button to renew your subscription.",
        isViewed: false,
        isSubscriptionAlert: true,
      };

      const response = await axios.post(
        "https://api.fitoja.in/api/create-notification",
        data
      );

      // Set isSubscriptionAlertSent to True
      setIsSubsciptionAlertTrue(userPhoneNumber, userName);
    } catch (error) {
      console.error("Failed to send subscription alert:", error);
      toast.error("Failed to send subscription alert.", {
        position: "top-right",
        autoClose: 7000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  };

  const setIsSubsciptionAlertTrue = async (
    userPhoneNumber: string,
    userName: string
  ) => {
    try {
      const data = {
        phoneNumber: userPhoneNumber,
        isSubscriptionAlertSent: true,
      };

      const response = await axios.put(
        "https://api.fitoja.in/api/edit-personal-info",
        data
      );

      // Show success toaster
      toast.success("Subscription Alert sent successfully!", {
        position: "top-right",
        autoClose: 7000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

      // Refresh the list
      fetchAllUsers();
    } catch (error) {
      console.error("Failed to send subscription alert:", error);
      toast.error("Failed to send subscription alert.", {
        position: "top-right",
        autoClose: 7000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
        progress: undefined,
      });
    }
  };

  return (
    <div className="subscription-alert">
      <ToastContainer theme="colored" /> {/* Toaster container */}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}
      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} alt="Logo" />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                  alt="Close"
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          <div className="search-box">
            <h2>Send Subscription Alert to subscribed users</h2>
            <input
              type="text"
              placeholder="Search user by Phone Number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="user-list">
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div key={user._id} className="user-card">
                  <div className="user-details">
                    <p>
                      <strong>Name:</strong> <span>{user.name}</span>
                    </p>
                    <p>
                      <strong>Phone Number:</strong>{" "}
                      <span>{user.phoneNumber}</span>
                    </p>
                    <p>
                      <strong>Email:</strong> <span>{user.email}</span>
                    </p>
                    <p>
                      <strong>Gender:</strong> <span>{user.gender}</span>
                    </p>
                    <p>
                      <strong>Age:</strong> <span>{user.age}</span>
                    </p>
                    <p>
                      <strong>Height:</strong> <span>{user.height}</span>
                    </p>
                    <p>
                      <strong>Weight:</strong> <span>{user.weight}</span>
                    </p>
                  </div>
                  <button
                    className="subscription-alert-btn"
                    onClick={() => {
                      sendSubscriptionAlert(user.phoneNumber, user.name);
                    }}
                  >
                    Send Subscription Alert
                  </button>
                </div>
              ))
            ) : (
              <p>No User found.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionAlert;
